import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';

export interface RunByUser {
  id: string;
  email: string | null;
  name: string | null;
}

export interface WorkflowRun {
  id: string;
  workflow_id: string;
  version_id: string;
  session_id: string;
  run_by_id: string;
  /** User who started the run, resolved server-side from run_by_id. */
  run_by?: RunByUser | null;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  trigger_type?: string;
  // Additional computed fields
  duration_seconds: number | null;
  /** Run cost in USD. `null` means unknown, not free. */
  cost: number | null;
  error_count: number;
}

export interface ListWorkflowRunsResponse {
  success?: boolean;
  message?: string;
  workflow_id?: string;
  runs?: WorkflowRun[];
  count?: number;
  detail?: string;
}

/**
 * Fetches the list of runs for a specific workflow from the v1 API.
 * @param workflowId - The workflow ID
 * @param orgId - The organization ID
 * @param limit - Maximum number of runs to fetch (default: 50)
 * @param offset - Pagination offset (default: 0)
 * @returns Promise containing the list of runs
 */
export async function listWorkflowRuns(
  workflowId: string,
  orgId: string,
  limit: number = 50,
  offset: number = 0,
): Promise<ListWorkflowRunsResponse> {
  try {
    const response = await apiRequest<ListWorkflowRunsResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/runs?limit=${limit}&offset=${offset}`,
      target: 'workflow',
      method: 'GET',
    });

    if (response?.detail) {
      return { detail: response.detail };
    }

    // The response.data contains the actual response
    const data = response.data || response;
    return data as ListWorkflowRunsResponse;
  } catch (error) {
    console.error('Error fetching workflow runs:', error);
    Sentry.captureException(error, {
      extra: { context: 'list-workflow-runs' },
    });
    throw error;
  }
}
